import * as XLSX from 'xlsx';
import Message from './message/Message';

/**
 * 엑셀 파일을 읽어서 JSON 데이터로 변환
 * @param {File} file - 업로드된 엑셀 파일
 * @param {Array} expectedColumns - 예상되는 컬럼 정보 [{field, headerName, required, type}]
 * @returns {Promise<Array>} 변환된 데이터 배열
 */
export const readExcelFile = async (file, expectedColumns) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('파일이 선택되지 않았습니다.'));
      return;
    }

    // 파일 확장자 검증
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls'].includes(fileExtension)) {
      reject(new Error('엑셀 파일(.xlsx, .xls)만 업로드 가능합니다.'));
      return;
    }

    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        if (workbook.SheetNames.length === 0) {
          reject(new Error('엑셀 파일에 시트가 없습니다.'));
          return;
        }
        
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // 엑셀 데이터를 JSON으로 변환
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
          header: 1,
          defval: '', // 빈 셀의 기본값
          raw: false // 날짜 등을 문자열로 변환
        });
        
        if (jsonData.length === 0) {
          reject(new Error('엑셀 파일에 데이터가 없습니다.'));
          return;
        }
        
        // 첫 번째 행을 헤더로 사용
        const headers = jsonData[0];
        const rows = jsonData.slice(1);
        
        if (headers.length === 0) {
          reject(new Error('헤더 정보가 없습니다.'));
          return;
        }
        
        if (rows.length === 0) {
          reject(new Error('데이터 행이 없습니다.'));
          return;
        }
        
        // 데이터 검증 및 변환
        const validatedData = validateAndTransformData(headers, rows, expectedColumns);
        
        resolve(validatedData);
      } catch (error) {
        console.error('엑셀 파일 읽기 오류:', error);
        reject(new Error(`엑셀 파일 처리 중 오류가 발생했습니다: ${error.message}`));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('파일 읽기 중 오류가 발생했습니다.'));
    };
    
    reader.readAsArrayBuffer(file);
  });
};

/**
 * 엑셀 데이터 검증 및 변환
 * @param {Array} headers - 엑셀 헤더 배열
 * @param {Array} rows - 엑셀 데이터 행 배열
 * @param {Array} expectedColumns - 예상되는 컬럼 정보
 * @returns {Array} 변환된 데이터
 */
const validateAndTransformData = (headers, rows, expectedColumns) => {
  // 헤더 검증
  const headerValidation = validateHeaders(headers, expectedColumns);
  if (!headerValidation.isValid) {
    throw new Error(headerValidation.message);
  }
  
  // 헤더와 컬럼 매핑
  const columnMapping = createColumnMapping(headers, expectedColumns);
  
  // 데이터 변환 및 검증
  const transformedData = [];
  const errors = [];
  
  rows.forEach((row, rowIndex) => {
    // 빈 행 건너뛰기
    if (isEmptyRow(row)) {
      return;
    }
    
    try {
      const transformedRow = transformRow(row, columnMapping, expectedColumns, rowIndex + 2);
      transformedData.push(transformedRow);
    } catch (error) {
      errors.push(`${rowIndex + 2}행: ${error.message}`);
    }
  });
  
  // 에러가 있으면 첫 번째 에러만 표시
  if (errors.length > 0) {
    throw new Error(errors[0]);
  }
  
  if (transformedData.length === 0) {
    throw new Error('유효한 데이터가 없습니다.');
  }
  
  return transformedData;
};

/**
 * 헤더 검증
 * @param {Array} headers - 엑셀 헤더
 * @param {Array} expectedColumns - 예상 컬럼
 * @returns {Object} 검증 결과
 */
const validateHeaders = (headers, expectedColumns) => {
  const requiredColumns = expectedColumns.filter(col => col.required !== false);
  const missingColumns = [];
  
  requiredColumns.forEach(col => {
    if (!headers.includes(col.headerName)) {
      missingColumns.push(col.headerName);
    }
  });
  
  if (missingColumns.length > 0) {
    return {
      isValid: false,
      message: `필수 컬럼이 누락되었습니다: ${missingColumns.join(', ')}`
    };
  }
  
  return { isValid: true };
};

/**
 * 헤더와 컬럼 매핑 생성
 * @param {Array} headers - 엑셀 헤더
 * @param {Array} expectedColumns - 예상 컬럼
 * @returns {Object} 매핑 객체
 */
const createColumnMapping = (headers, expectedColumns) => {
  const mapping = {};
  
  expectedColumns.forEach(col => {
    const headerIndex = headers.findIndex(header => header === col.headerName);
    if (headerIndex !== -1) {
      mapping[col.field] = {
        index: headerIndex,
        ...col
      };
    }
  });
  
  return mapping;
};

/**
 * 빈 행 확인
 * @param {Array} row - 데이터 행
 * @returns {boolean} 빈 행 여부
 */
const isEmptyRow = (row) => {
  return !row || row.every(cell => !cell || cell.toString().trim() === '');
};

/**
 * 데이터 행 변환
 * @param {Array} row - 엑셀 데이터 행
 * @param {Object} columnMapping - 컬럼 매핑
 * @param {Array} expectedColumns - 예상 컬럼
 * @param {number} rowNumber - 행 번호
 * @returns {Object} 변환된 행 데이터
 */
const transformRow = (row, columnMapping, expectedColumns, rowNumber) => {
  const transformedRow = {};
  
  // 각 컬럼별로 데이터 변환
  Object.keys(columnMapping).forEach(field => {
    const colInfo = columnMapping[field];
    const cellValue = row[colInfo.index];
    
    try {
      transformedRow[field] = transformCellValue(cellValue, colInfo, field, rowNumber);
    } catch (error) {
      throw new Error(`${colInfo.headerName} 컬럼: ${error.message}`);
    }
  });
  
  // 행 번호 추가 (에러 추적용)
  transformedRow._rowNumber = rowNumber;
  transformedRow._isExcelData = true;
  
  return transformedRow;
};

/**
 * 셀 값 변환
 * @param {*} cellValue - 셀 값
 * @param {Object} colInfo - 컬럼 정보
 * @param {string} field - 필드명
 * @param {number} rowNumber - 행 번호
 * @returns {*} 변환된 값
 */
const transformCellValue = (cellValue, colInfo, field, rowNumber) => {
  // 빈 값 처리
  if (cellValue === null || cellValue === undefined || cellValue === '') {
    if (colInfo.required !== false) {
      throw new Error('필수 입력 항목입니다.');
    }
    return colInfo.defaultValue || '';
  }
  
  const stringValue = cellValue.toString().trim();
  
  // 타입별 변환
  switch (colInfo.type) {
    case 'number':
      const numValue = parseFloat(stringValue);
      if (isNaN(numValue)) {
        throw new Error('숫자 형식이 아닙니다.');
      }
      return numValue;
      
    case 'integer':
      const intValue = parseInt(stringValue, 10);
      if (isNaN(intValue)) {
        throw new Error('정수 형식이 아닙니다.');
      }
      return intValue;
      
    case 'date':
      try {
        // 엑셀 날짜 형식 처리
        if (typeof cellValue === 'number') {
          // 엑셀 시리얼 날짜
          const date = XLSX.SSF.parse_date_code(cellValue);
          return new Date(date.y, date.m - 1, date.d);
        } else {
          // 문자열 날짜
          const date = new Date(stringValue);
          if (isNaN(date.getTime())) {
            throw new Error('날짜 형식이 올바르지 않습니다.');
          }
          return date;
        }
      } catch (error) {
        throw new Error('날짜 형식이 올바르지 않습니다.');
      }
      
    case 'boolean':
      const lowerValue = stringValue.toLowerCase();
      if (['true', '1', 'yes', 'y', '예', 'o'].includes(lowerValue)) {
        return true;
      } else if (['false', '0', 'no', 'n', '아니오', 'x'].includes(lowerValue)) {
        return false;
      } else {
        throw new Error('불린 값이 아닙니다. (true/false, 1/0, 예/아니오)');
      }
      
    case 'email':
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(stringValue)) {
        throw new Error('이메일 형식이 올바르지 않습니다.');
      }
      return stringValue;
      
    case 'phone':
      const phoneRegex = /^[\d\-\(\)\+\s]+$/;
      if (!phoneRegex.test(stringValue)) {
        throw new Error('전화번호 형식이 올바르지 않습니다.');
      }
      return stringValue;
      
    default:
      // 기본적으로 문자열로 처리
      if (colInfo.maxLength && stringValue.length > colInfo.maxLength) {
        throw new Error(`최대 ${colInfo.maxLength}자까지 입력 가능합니다.`);
      }
      return stringValue;
  }
};

/**
 * 엑셀 템플릿 생성 및 다운로드
 * @param {Array} columns - 컬럼 정의
 * @param {string} filename - 파일명
 * @param {Array} sampleData - 샘플 데이터 (선택사항)
 */
export const downloadExcelTemplate = (columns, filename, sampleData = []) => {
  try {
    // 매개변수 검증
    if (!columns || !Array.isArray(columns) || columns.length === 0) {
      throw new Error('컬럼 정보가 올바르지 않습니다.');
    }

    if (!filename || typeof filename !== 'string') {
      throw new Error('파일명이 올바르지 않습니다.');
    }

    // 헤더 생성
    const headers = columns.map(col => col.headerName || col.field || '컬럼');
    
    // 샘플 데이터 처리
    let processedSampleData = [];
    
    if (sampleData && sampleData.length > 0) {
      // sampleData가 객체 배열인 경우 배열의 배열로 변환
      if (typeof sampleData[0] === 'object' && sampleData[0] !== null) {
        processedSampleData = sampleData.map(item => {
          return columns.map(col => {
            const field = col.field || col.headerName;
            return item[field] || '';
          });
        });
      } 
      // 이미 배열의 배열인 경우 그대로 사용
      else if (Array.isArray(sampleData[0])) {
        processedSampleData = sampleData;
      }
      // 기타 경우는 빈 배열로 처리
      else {
        processedSampleData = [];
      }
    }
    
    // 템플릿 데이터 생성: 헤더 + 샘플데이터 또는 빈 행 3개
    const templateData = processedSampleData.length > 0 
      ? [headers, ...processedSampleData]
      : [headers, ...Array(3).fill(null).map(() => Array(headers.length).fill(''))];
    
    console.log('템플릿 데이터 구조:', templateData); // 디버깅용
    
    // 워크북 생성
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(templateData);
    
    // 컬럼 너비 설정
    const colWidths = columns.map(col => ({
      wch: Math.max((col.headerName || col.field || '컬럼').length + 2, 15)
    }));
    ws['!cols'] = colWidths;
    
    // 헤더 스타일 설정 (선택사항)
    const headerRange = XLSX.utils.decode_range(ws['!ref']);
    for (let col = headerRange.s.c; col <= headerRange.e.c; col++) {
      const cellRef = XLSX.utils.encode_cell({r: 0, c: col});
      if (ws[cellRef]) {
        ws[cellRef].s = {
          font: { bold: true },
          fill: { fgColor: { rgb: "CCCCCC" } },
          alignment: { horizontal: "center" }
        };
      }
    }
    
    // 워크시트를 워크북에 추가
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    
    // 파일 다운로드
    const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const fullFilename = `${filename}_템플릿_${timestamp}.xlsx`;
    XLSX.writeFile(wb, fullFilename);
    
    return true;
  } catch (error) {
    console.error('템플릿 생성 오류:', error);
    Message.showError('템플릿 다운로드 중 오류가 발생했습니다.');
    return false;
  }
}; 